import scaleCartImage from "@/assets/products/studio/scale.webp?w=400&format=webp";
import scale4GCartImage from "@/assets/products/studio/scale-4g.webp?w=400&format=webp";
import nfcCartImage from "@/assets/products/studio/nfc-tag.webp?w=400&format=webp";
import recorderCartImage from "@/assets/products/studio/recorder.webp?w=400&format=webp";
import React, { createContext, useContext, useState, useEffect } from "react";
import { CURRENCY_LABEL, PRODUCT_PRICES } from "@/lib/currency";
import { useCurrency } from "@/contexts/CurrencyContext";
import { trackEvent } from "@/lib/analytics";

export interface Product {
  id: string;
  name: string;
  nameEn: string;
  namePl: string;
  nameDe: string;
  price: number;
  image: string;
  descriptionEn: string;
  descriptionPl: string;
  descriptionDe: string;
  inStock: boolean;
  hidden?: boolean;
  restocking?: boolean;
  weightKg: number;
  category: string;
  specsEn: Record<string, string>;
  specsPl: Record<string, string>;
  specsDe: Record<string, string>;
  isPhysical: boolean;
}

export interface CartItem {
  product: Product;
  quantity: number;
}

export const PRODUCTS: Product[] = [
  {
    "id": "Scale4G",
    "name": "Fluxabee 4G Hive Scale",
    "nameEn": "Fluxabee 4G Hive Scale",
    "namePl": "Waga pasieczna Fluxabee 4G",
    "nameDe": "Fluxabee 4G Bienenstock-Waage",
    "price": 1999,
    "descriptionEn": "Solar-powered hive scale with 4G + 2G connectivity. Flush solar panel and antenna inside the frame.",
    "descriptionPl": "Waga pasieczna z panelem solarnym i łącznością 4G + 2G. Zlicowany panel i antena wewnątrz konstrukcji.",
    "descriptionDe": "Bienenstock-Waage mit Solarmodul und 4G + 2G-Verbindung. Bündiges Solarmodul und Antenne innerhalb des Rahmens.",
    "inStock": false,
    "restocking": true,
    "weightKg": 4,
    "category": "hardware",
    "isPhysical": true,
    "specsEn": {
      "Connectivity": "4G + 2G",
      "Measurements": "Hive weight and ambient temperature",
      "Measurement interval": "Every hour",
      "Load cells": "4 class C3 sensors",
      "Power": "Solar panel and rechargeable battery"
    },
    "specsPl": {
      "Łączność": "4G + 2G",
      "Pomiary": "Masa ula i temperatura otoczenia",
      "Częstotliwość pomiaru": "Co godzinę",
      "Czujniki": "4 czujniki klasy C3",
      "Zasilanie": "Panel solarny i akumulator"
    },
    "specsDe": {
      "Verbindung": "4G + 2G",
      "Messungen": "Stockgewicht und Umgebungstemperatur",
      "Messintervall": "Stündlich",
      "Wägezellen": "4 Sensoren der Klasse C3",
      "Stromversorgung": "Solarmodul und Akku"
    },
    "image": scale4GCartImage
  },
  {
    "id": "Scale",
    "name": "Fluxabee 2G Hive Scale",
    "nameEn": "Fluxabee 2G Hive Scale",
    "namePl": "Waga pasieczna Fluxabee 2G",
    "nameDe": "Fluxabee 2G Bienenstock-Waage",
    "price": 1299,
    "descriptionEn": "Solar-powered hive scale with 2G connectivity. Clearance of the last units in stock. Available in Poland only.",
    "descriptionPl": "Waga pasieczna z panelem solarnym i łącznością 2G. Wyprzedaż ostatnich sztuk magazynowych. Dostępna tylko w Polsce.",
    "descriptionDe": "Bienenstock-Waage mit Solarmodul und 2G-Verbindung. Abverkauf der letzten Lagerbestände. Nur in Polen erhältlich.",
    "inStock": true,
    "weightKg": 4,
    "category": "hardware",
    "isPhysical": true,
    "specsEn": {
      "Connectivity": "2G",
      "Measurements": "Hive weight and ambient temperature",
      "Measurement interval": "Every hour",
      "Load cells": "4 class C3 sensors",
      "Power": "Solar panel and rechargeable battery"
    },
    "specsPl": {
      "Łączność": "2G",
      "Pomiary": "Masa ula i temperatura otoczenia",
      "Częstotliwość pomiaru": "Co godzinę",
      "Czujniki": "4 czujniki klasy C3",
      "Zasilanie": "Panel solarny i akumulator"
    },
    "specsDe": {
      "Verbindung": "2G",
      "Messungen": "Stockgewicht und Umgebungstemperatur",
      "Messintervall": "Stündlich",
      "Wägezellen": "4 Sensoren der Klasse C3",
      "Stromversorgung": "Solarmodul und Akku"
    },
    "image": scaleCartImage
  },
  {
    id: "nfc-tag",
    name: "Flux Tag",
    nameEn: "Flux Tag",
    namePl: "Flux Tag",
    nameDe: "Flux Tag",
    price: PRODUCT_PRICES.pln["nfc-tag"],
    image: nfcCartImage,
    descriptionEn:
      "Pre-programmed NFC sticker for your hive. Assign it in the Fluxabee app, then scan it to hear saved queen details, problems and tasks in the app’s language.",
    descriptionPl:
      "Gotowa do przypisania naklejka NFC na ul. Połącz ją z ulem w aplikacji Fluxabee, a przy kolejnych skanach usłyszysz zapisane informacje o matce, problemy i zadania w języku aplikacji.",
    descriptionDe:
      "Vorprogrammierter NFC-Aufkleber für deinen Stock. In der Fluxabee-App zuweisen und bei weiteren Scans gespeicherte Königinneninfos, Probleme und Aufgaben in der App-Sprache anhören.",
    inStock: true,
    weightKg: 0.005,
    category: "accessory",
    isPhysical: true,
    specsEn: {
      "Diameter": "29 mm",
      "Thickness": "0.8 mm",
      "Material": "Adhesive PET, weather-resistant",
      "Operating Temp": "-25°C to +70°C",
      "Compatibility": "NFC tag reading + current Fluxabee app (iOS / Android)",
      "Languages": "Matches the app language",
    },
    specsPl: {
      "Średnica": "29 mm",
      "Grubość": "0,8 mm",
      "Materiał": "Naklejka PET, odporna na pogodę",
      "Temperatura pracy": "-25°C do +70°C",
      "Kompatybilność": "Odczyt NFC + aktualna aplikacja Fluxabee (iOS / Android)",
      "Języki": "Zgodnie z językiem aplikacji",
    },
    specsDe: {
      "Durchmesser": "29 mm",
      "Dicke": "0,8 mm",
      "Material": "Klebe-PET, witterungsbeständig",
      "Betriebstemperatur": "-25°C bis +70°C",
      "Kompatibilität": "NFC-Lesefunktion + aktuelle Fluxabee-App (iOS / Android)",
      "Sprachen": "Entsprechend der App-Sprache",
    },
  },
  {
    id: "recorder",
    name: "Fluxabee Recorder",
    nameEn: "Fluxabee Recorder",
    namePl: "Fluxabee Recorder",
    nameDe: "Fluxabee Recorder",
    price: PRODUCT_PRICES.pln.recorder,
    image: recorderCartImage,
    descriptionEn:
      "A voice-activated recorder for beekeepers. Talk during inspections; Fluxabee AI turns your words into notes, tasks and hive statuses.",
    descriptionPl:
      "Dyktafon z aktywacją głosem dla pszczelarzy. Mów podczas przeglądów, a AI Fluxabee zamieni słowa w notatki, zadania i statusy uli.",
    descriptionDe:
      "Ein sprachaktivierter Rekorder für Imker. Sprich während der Durchsicht, und die Fluxabee-KI macht daraus Notizen, Aufgaben und Stock-Status.",
    inStock: false,
    weightKg: 0.05,
    category: "hardware",
    isPhysical: true,
    specsEn: {
      "Storage": "128 GB",
      "Recording mode": "Voice activation (VOX)",
      "Battery": "Days in standby",
      "Connectivity": "USB-C / OTG",
      "Audio format": "WAV / MP3",
      "Weight": "approx. 30 g",
    },
    specsPl: {
      "Pamięć": "128 GB",
      "Tryb nagrywania": "Aktywacja głosem (VOX)",
      "Czas pracy": "Wiele dni w czuwaniu",
      "Łączność": "USB-C / OTG",
      "Format audio": "WAV / MP3",
      "Waga": "ok. 30 g",
    },
    specsDe: {
      "Speicher": "128 GB",
      "Aufnahmemodus": "Sprachaktivierung (VOX)",
      "Akku": "Tagelang im Standby",
      "Konnektivität": "USB-C / OTG",
      "Audioformat": "WAV / MP3",
      "Gewicht": "ca. 30 g",
    },
  },
];

export const VISIBLE_PRODUCTS = PRODUCTS.filter((product) => !product.hidden);

export interface AppliedDiscount {
  // The validated discount code.
  code: string;
  // Discount amount for the currency it was validated in, in the SMALLEST
  // unit (grosz / euro-cents), exactly as returned by the backend.
  amount: number;
  // Currency the amount applies to - used to detect currency changes.
  currency: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  discount: AppliedDiscount | null;
  setDiscount: (discount: AppliedDiscount | null) => void;
}

const DISCOUNT_STORAGE_KEY = "fluxabee-cart-discount";

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // SSR-safe: initial render is empty cart; hydrate from localStorage on mount
  // so prerendered HTML + browser runtime start from identical state.
  const [items, setItems] = useState<CartItem[]>([]);
  const [discount, setDiscount] = useState<AppliedDiscount | null>(null);
  const hydratedRef = React.useRef(false);
  const { currency } = useCurrency();

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem("fluxabee-cart");
      if (saved) {
        const parsed: CartItem[] = JSON.parse(saved);
        // Merge with current PRODUCTS to pick up new fields (e.g. weightKg) added after caching.
        // Drop unavailable products and unrecognized legacy IDs (e.g. lowercase "scale").
        setItems(
          parsed.flatMap((item) => {
            const current = PRODUCTS.find((p) => p.id === item.product.id);
            return current && !current.hidden && current.inStock
              ? [{ ...item, product: current }]
              : [];
          })
        );
      }
    } catch {
      // Ignore - cart stays empty on parse errors.
    }
    try {
      const savedDiscount = localStorage.getItem(DISCOUNT_STORAGE_KEY);
      if (savedDiscount) {
        setDiscount(JSON.parse(savedDiscount) as AppliedDiscount);
      }
    } catch {
      // Ignore - no discount applied on parse errors.
    } finally {
      hydratedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Don't overwrite storage before hydration completes - otherwise the
    // empty initial state would clobber a returning user's saved cart.
    if (!hydratedRef.current) return;
    localStorage.setItem("fluxabee-cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hydratedRef.current) return;
    if (discount) {
      localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(discount));
    } else {
      localStorage.removeItem(DISCOUNT_STORAGE_KEY);
    }
  }, [discount]);

  const addToCart = (product: Product, quantity = 1) => {
    const current = PRODUCTS.find((candidate) => candidate.id === product.id);
    if (!current || current.hidden || !current.inStock) return;

    setItems((prev) => {
      const existing = prev.find((i) => i.product.id === current.id);
      if (existing) {
        return prev.map((i) =>
          i.product.id === current.id ? { product: current, quantity: i.quantity + quantity } : i
        );
      }
      return [...prev, { product: current, quantity }];
    });

    // GA4: price in user's current currency (not the hardcoded PLN on Product).
    const unitPrice = PRODUCT_PRICES[currency]?.[current.id] ?? current.price;
    trackEvent("add_to_cart", {
      currency: CURRENCY_LABEL[currency],
      value: unitPrice * quantity,
      items: [
        {
          item_id: current.id,
          item_name: current.nameEn,
          item_category: current.category,
          price: unitPrice,
          quantity,
        },
      ],
    });
  };

  const removeFromCart = (productId: string) => {
    setItems((prev) => prev.filter((i) => i.product.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.product.id === productId ? { ...i, quantity } : i))
    );
  };

  const clearCart = () => {
    setItems([]);
    setDiscount(null);
  };

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalItems, totalPrice, discount, setDiscount }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
